import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { User, Heart, Phone, Shield, Stethoscope, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { useDemo } from '@/contexts/DemoContext';

const ProfileTab = () => {
  const { isDemoMode, demoData } = useDemo();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    preferred_name: '',
    date_of_birth: '',
    sex: '',
    blood_type: '',
    organ_donor: false,
    emergency_contact_name: '',
    emergency_contact_relationship: '',
    emergency_contact_phone: '',
    insurance_provider: '',
    insurance_plan: '',
    insurance_member_id: '',
    insurance_group: '',
    pcp_name: '',
    pcp_clinic: '',
    pcp_phone: '',
  });

  useEffect(() => {
    if (isDemoMode) {
      loadDemoProfile();
    } else {
      fetchProfile();
    }
  }, [isDemoMode]);

  const loadDemoProfile = () => {
    // Load demo profile for Michael Carter from demoData
    const demoUser = demoData?.user || {};
    const demoMedical = demoData?.medicalInfo || {};
    const demoInsurance = demoData?.insurance?.[0] || {};
    const demoPCP = demoData?.doctors?.[0] || {};
    const demoContact = demoData?.emergencyContacts?.[0] || {};
    
    const demoProfile = {
      full_name: demoUser.name || 'Michael Carter',
      preferred_name: 'Mike',
      date_of_birth: demoMedical.dateOfBirth || '1978-03-15',
      sex: 'Male',
      blood_type: demoMedical.bloodType || 'O+',
      emergency_contact_name: demoContact.name || 'Emily Carter',
      emergency_contact_relationship: demoContact.relationship || 'Wife',
      emergency_contact_phone: demoContact.phone || '(555) 234-5678',
      insurance_provider: demoInsurance.provider || 'Blue Cross Blue Shield',
      insurance_plan: demoInsurance.plan || 'PPO Gold Family Plan',
      insurance_member_id: demoInsurance.member_id || 'XYZ123456789',
      insurance_group: demoInsurance.group_number || 'BC-FAMILY-001',
      pcp_name: demoPCP.name || 'Dr. Sarah Chen',
      pcp_clinic: demoPCP.practice || 'Oakwood Family Medicine',
      pcp_phone: demoPCP.phone || '(555) 456-7890',
      organ_donor: demoMedical.organ_donor !== undefined ? demoMedical.organ_donor : true,
    };
    
    setProfile(demoProfile);
    setFormData(demoProfile);
    setLoading(false);
  };

  const fetchProfile = async () => {
    try {
      const response = await API.get('/medical/profile');
      setProfile(response.data);
      setFormData({
        full_name: response.data.full_name || '',
        preferred_name: response.data.preferred_name || '',
        date_of_birth: response.data.date_of_birth || '',
        sex: response.data.sex || '',
        blood_type: response.data.blood_type || '',
        organ_donor: response.data.organ_donor || false,
        emergency_contact_name: response.data.emergency_contact_name || '',
        emergency_contact_relationship: response.data.emergency_contact_relationship || '',
        emergency_contact_phone: response.data.emergency_contact_phone || '',
        insurance_provider: response.data.insurance_provider || '',
        insurance_plan: response.data.insurance_plan || '',
        insurance_member_id: response.data.insurance_member_id || '',
        insurance_group: response.data.insurance_group || '',
        pcp_name: response.data.pcp_name || '',
        pcp_clinic: response.data.pcp_clinic || '',
        pcp_phone: response.data.pcp_phone || '',
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      // Don't show error toast - just silently fail and show empty form
      // User can still fill it out
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    
    // In demo mode, just update local state
    if (isDemoMode) {
      setProfile(formData);
      toast.success('Profile updated (demo mode)');
      setSaving(false);
      return;
    }
    
    try {
      await API.put('/medical/profile', formData);
      toast.success('Profile updated successfully');
      fetchProfile();
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const bloodTypes = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
  const sexOptions = ['Male', 'Female', 'Other', 'Prefer not to say'];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Medical Profile</h3>
          <p className="text-sm text-gray-500">This information appears on your PDF exports for doctor visits</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900"
          data-testid="save-profile-btn"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {saving ? 'Saving...' : 'Save Profile'}
        </Button>
      </div>

      {/* Personal Information */}
      <Card className="border-gray-100">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-[#2E7D6A]" />
            <CardTitle className="text-base">Personal Information</CardTitle>
          </div>
          <CardDescription>Basic identifying information for medical records</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label htmlFor="full_name">Full Legal Name</Label>
            <Input
              id="full_name"
              value={formData.full_name}
              onChange={(e) => handleChange('full_name', e.target.value)}
              placeholder={isDemoMode ? "Michael Carter" : "Enter full name"}
              data-testid="input-full-name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="preferred_name">Preferred Name</Label>
            <Input
              id="preferred_name"
              value={formData.preferred_name}
              onChange={(e) => handleChange('preferred_name', e.target.value)}
              placeholder={isDemoMode ? "Mike" : "Nickname or preferred name"}
              data-testid="input-preferred-name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="date_of_birth">Date of Birth</Label>
            <Input
              id="date_of_birth"
              type="date"
              value={formData.date_of_birth}
              onChange={(e) => handleChange('date_of_birth', e.target.value)}
              data-testid="input-dob"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sex">Sex</Label>
            <Select value={formData.sex} onValueChange={(v) => handleChange('sex', v)}>
              <SelectTrigger data-testid="select-sex">
                <SelectValue placeholder="Select sex" />
              </SelectTrigger>
              <SelectContent>
                {sexOptions.map(opt => (
                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Medical Info */}
      <Card className="border-gray-100">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-red-500" />
            <CardTitle className="text-base">Medical Information</CardTitle>
          </div>
          <CardDescription>Critical medical info for emergencies</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="blood_type">Blood Type</Label>
            <Select value={formData.blood_type} onValueChange={(v) => handleChange('blood_type', v)}>
              <SelectTrigger data-testid="select-blood-type">
                <SelectValue placeholder="Select blood type" />
              </SelectTrigger>
              <SelectContent>
                {bloodTypes.map(bt => (
                  <SelectItem key={bt} value={bt}>{bt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div 
            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer"
            onClick={() => handleChange('organ_donor', !formData.organ_donor)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleChange('organ_donor', !formData.organ_donor)}
          >
            <div className="space-y-0.5">
              <span className="text-sm font-medium">
                Organ Donor
              </span>
              <p className="text-xs text-slate-500">Registered organ donor</p>
            </div>
            <Switch
              id="organ_donor"
              checked={formData.organ_donor || false}
              onCheckedChange={(checked) => handleChange('organ_donor', checked)}
              onClick={(e) => e.stopPropagation()}
              data-testid="organ-donor-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Emergency Contact */}
      <Card className="border-gray-100">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-orange-500" />
            <CardTitle className="text-base">Emergency Contact</CardTitle>
          </div>
          <CardDescription>Who to contact in case of emergency</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="emergency_contact_name">Contact Name</Label>
            <Input
              id="emergency_contact_name"
              value={formData.emergency_contact_name}
              onChange={(e) => handleChange('emergency_contact_name', e.target.value)}
              placeholder="Jane Doe"
              data-testid="input-ec-name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="emergency_contact_relationship">Relationship</Label>
            <Input
              id="emergency_contact_relationship"
              value={formData.emergency_contact_relationship}
              onChange={(e) => handleChange('emergency_contact_relationship', e.target.value)}
              placeholder="Spouse, Parent, etc."
              data-testid="input-ec-relationship"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="emergency_contact_phone">Phone Number</Label>
            <Input
              id="emergency_contact_phone"
              value={formData.emergency_contact_phone}
              onChange={(e) => handleChange('emergency_contact_phone', e.target.value)}
              placeholder="(555) 123-4567"
              data-testid="input-ec-phone"
            />
          </div>
        </CardContent>
      </Card>

      {/* Insurance Information */}
      <Card className="border-gray-100">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-500" />
            <CardTitle className="text-base">Insurance Information</CardTitle>
          </div>
          <CardDescription>Health insurance details for billing</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label htmlFor="insurance_provider">Insurance Provider</Label>
            <Input
              id="insurance_provider"
              value={formData.insurance_provider}
              onChange={(e) => handleChange('insurance_provider', e.target.value)}
              placeholder="Blue Cross Blue Shield"
              data-testid="input-insurance-provider"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="insurance_plan">Plan Name</Label>
            <Input
              id="insurance_plan"
              value={formData.insurance_plan}
              onChange={(e) => handleChange('insurance_plan', e.target.value)}
              placeholder="PPO Gold"
              data-testid="input-insurance-plan"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="insurance_member_id">Member ID</Label>
            <Input
              id="insurance_member_id"
              value={formData.insurance_member_id}
              onChange={(e) => handleChange('insurance_member_id', e.target.value)}
              placeholder="ABC123456789"
              data-testid="input-insurance-member-id"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="insurance_group">Group Number</Label>
            <Input
              id="insurance_group"
              value={formData.insurance_group}
              onChange={(e) => handleChange('insurance_group', e.target.value)}
              placeholder="GRP001"
              data-testid="input-insurance-group"
            />
          </div>
        </CardContent>
      </Card>

      {/* Primary Care Provider */}
      <Card className="border-gray-100">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5 text-[#2E7D6A]" />
            <CardTitle className="text-base">Primary Care Provider</CardTitle>
          </div>
          <CardDescription>Your main doctor's information</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="pcp_name">Doctor's Name</Label>
            <Input
              id="pcp_name"
              value={formData.pcp_name}
              onChange={(e) => handleChange('pcp_name', e.target.value)}
              placeholder="Dr. Sarah Smith"
              data-testid="input-pcp-name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pcp_clinic">Clinic / Practice</Label>
            <Input
              id="pcp_clinic"
              value={formData.pcp_clinic}
              onChange={(e) => handleChange('pcp_clinic', e.target.value)}
              placeholder="Downtown Family Medicine"
              data-testid="input-pcp-clinic"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pcp_phone">Office Phone</Label>
            <Input
              id="pcp_phone"
              value={formData.pcp_phone}
              onChange={(e) => handleChange('pcp_phone', e.target.value)}
              placeholder="(555) 987-6543"
              data-testid="input-pcp-phone"
            />
          </div>
        </CardContent>
      </Card>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Tip:</strong> This information will appear in the header of all your PDF exports, 
          making it easy to share your medical profile with healthcare providers.
        </p>
      </div>
    </div>
  );
};

export default ProfileTab;
